import CategorySelector from '@/components/Explore/CategorySelector'
import FullModalContainer from '@/components/FullModalContainer'
import Loading from '@/components/Loading'
import FilterModal from '@/components/PCP/FilterModal'
import RootLayout from '@/layouts/RootLayout'
import { useTranslation } from 'next-i18next'
import NextImage from 'next/image'
import { useRouter } from 'next/router'
import querystring from 'querystring'
import { useEffect, useState } from 'react'
import ReactVisibilitySensor from 'react-visibility-sensor'
import { Image, PageInfo, Product } from 'shopify/api'
import BannerSection from '../PCP/BannerSection'
import ProductListCard from '../PCP/ProductListCard'
import TextileRightAction from '../TextileRightAction'
import WifModal from '../WifModal'
import { WifNotification } from '../WifNotification'
import CountdownTimer from '../utilities/CountdownTimer'
import Icon from '../utilities/Icon'
import { FilterBar } from './FilterBar'
import { SortModal } from './SortModal'

interface ProductListPageContentsProps {
  products: Product[]
  productsLoading?: boolean
  onLoadMore: () => void
  pageInfo: PageInfo
  onRefetch: ({ category }: { category?: string }) => void
  bannerImage?: Image | null
  from: string
  collectionHandle?: string
  onRefetchSort: (sortVar: string) => void
  showFabricTypeSelector?: boolean
  withTabBar: boolean
  withLogo?: boolean
  backButton?: boolean
  title?: string
  description?: string
  descriptionHtml?: string
  isCampaign?: boolean
  campaignEndTime?: Date
  searchPlaceholder?: string
  searchPageHref?: string
}

export const ProductListPageContents = ({
  products,
  productsLoading = false,
  onLoadMore,
  pageInfo,
  onRefetch,
  bannerImage,
  from,
  collectionHandle,
  onRefetchSort,
  showFabricTypeSelector = false,
  withTabBar,
  withLogo,
  backButton,
  title,
  description,
  descriptionHtml,
  isCampaign,
  campaignEndTime,
  searchPlaceholder,
  searchPageHref,
}: ProductListPageContentsProps) => {
  const { t } = useTranslation('pcp')
  const [isSortOpen, setIsSortOpen] = useState(false)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false)
  const [activeFilters, setActiveFilters] = useState(0)
  const [activeSort, setActiveSort] = useState('')
  const [stop, setStop] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isModalDescOpen, setIsModalDescOpen] = useState<boolean>(false)

  const toggleFilterModal = () => setIsFilterModalOpen(!isFilterModalOpen)

  // Setup router to process query urls every router.push
  const router = useRouter()
  if (!router.isReady) return <div>loading</div>

  // Read the url parameters and process it for filtering
  let initialActiveFilters = 0
  let initialActiveSort = ''
  let currentCategoryQuery
  if (typeof window !== 'undefined') {
    const currentQueries = window.location.search.replace('?', '')
    const parsedQueries = querystring.decode(currentQueries)
    const { category, color, size, lowestPrice, highestPrice, sort } = parsedQueries
    currentCategoryQuery = category
    const parsedFilterQueries = {
      category,
      color,
      size,
      lowestPrice,
      highestPrice,
    }
    initialActiveFilters = Object.keys(parsedFilterQueries).reduce((total, query) => {
      // @ts-ignore
      if (!parsedFilterQueries[query]) return total
      return total + 1
    }, 0)
    initialActiveSort = sort as string
  }

  // Toggle sort modal on/off
  const toggleSort = () => {
    setIsSortOpen(!isSortOpen)
  }

  useEffect(() => {
    setActiveFilters(initialActiveFilters)
    setActiveSort(initialActiveSort)
  }, [initialActiveFilters, initialActiveSort])

  useEffect(() => {
    stop && setIsModalOpen(true)
  }, [stop])

  const handleSelectCategory = (selectedDecodedQueryParam: string) => {
    const searchParams = new URLSearchParams(window.location.search)
    if (!selectedDecodedQueryParam) {
      searchParams.delete('category')
    } else {
      const encodedCategoryQueryParam = encodeURIComponent(selectedDecodedQueryParam)
      searchParams.set('category', encodedCategoryQueryParam)
    }

    const newParams = searchParams.toString()
    const newUrl = `${window.location.pathname}?${newParams}`
    const categoryFilter = {
      category: selectedDecodedQueryParam,
    }
    onRefetch(categoryFilter)
    router.push(newUrl)
  }

  return (
    <RootLayout
      from={from}
      withTabBar={withTabBar}
      withLogo={true}
      backButton={backButton}
      title={title}
      description={description}
      searchPageHref={searchPageHref}
      searchPlaceholder={searchPlaceholder}
      rightActions={<TextileRightAction />}
    >
      <div className='max-w-[500px] mx-auto md:max-w-full w-full relative  overflow-hidden'>
        {bannerImage && (
          <BannerSection
            imgSrc={bannerImage?.url}
            alt={bannerImage?.altText || 'Collection Banner'}
            id={bannerImage?.id!}
            height={107}
          />
        )}
      </div>

      {descriptionHtml && (
        <div className='p-4'>
          <h2 className='text-sm font-bold uppercase mb-[8px]'>{title}</h2>
          <div
            className='text-sm font-light h-[62px] overflow-hidden'
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
          <div
            className='flex flex-nowrap text-center capitalize text-sm font-bold text-wif-orange cursor-pointer gap-2'
            onClick={() => setIsModalDescOpen(true)}
          >
            <div>{t('mto:more')}</div>
            <Icon
              iconSrc={'/icons/ic-chevron-right-wif-orange.svg'}
              width={7}
              height={5}
              alt={t('product_info.more')}
            />
          </div>
        </div>
      )}

      {isModalDescOpen && (
        <FullModalContainer
          id='description-modal'
          title={title || 'mto:description'}
          onClose={() => setIsModalDescOpen(false)}
        >
          <div
            className='text-sm font-light px-4'
            dangerouslySetInnerHTML={{ __html: descriptionHtml || '' }}
          />
        </FullModalContainer>
      )}

      {isCampaign && !!campaignEndTime && (
        <>
          {!stop && (
            <div className='grid gap-2 m-4 px-4  grid-flow-col bg-wif-orange rounded-full text-white text-xs  w-auto h-6 items-center'>
              <div className='font-normal'>{t('limited-sale-time:time_limit')}</div>
              <div className='flex items-center justify-end flex-nowrap gap-2 font-bold'>
                <NextImage src={'/icons/ic-clock.svg'} width={14} height={14} alt='Clock Icon' />
                <CountdownTimer endDate={campaignEndTime!} setStop={setStop} />
              </div>
            </div>
          )}
        </>
      )}
      {isModalOpen && (
        <WifModal
          id='limited-sale-time'
          bodyText={t('limited-sale-time:Campaign-ended')}
          mainActionText={t('limited-sale-time:Campaign-ended-ok')}
          onMainAction={async () => {
            router.push('/')
            setIsModalOpen(false)
          }}
        />
      )}

      {showFabricTypeSelector && (
        <CategorySelector
          onSelectCategory={handleSelectCategory}
          decodedActiveCategoryQuery={decodeURIComponent(currentCategoryQuery as string)}
        />
      )}

      <FilterBar
        activeFilters={activeFilters}
        activeSort={activeSort}
        toggleFilter={toggleFilterModal}
        toggleSort={toggleSort}
      />
      {isSortOpen && (
        <SortModal
          toggleSort={toggleSort}
          activeSort={activeSort}
          onRefetch={onRefetchSort}
          hasTabBar={withTabBar}
        />
      )}

      {productsLoading ? (
        <Loading />
      ) : (
        <>
          <ProductListCard id='collection' products={products} />
          {pageInfo?.hasNextPage && (
            <ReactVisibilitySensor onChange={onLoadMore}>
              <div className='text-center'>{t('loading_more')}</div>
            </ReactVisibilitySensor>
          )}
        </>
      )}

      {isFilterModalOpen && (
        <FullModalContainer id='filter-modal' title='Filter' onClose={toggleFilterModal}>
          <FilterModal
            id='pcp'
            collectionHandle={collectionHandle as string}
            handleClose={toggleFilterModal}
            onRefetch={onRefetch}
          />
        </FullModalContainer>
      )}
      <WifNotification />
    </RootLayout>
  )
}
